import classnames from 'classnames';
import React, { FC, useEffect, useRef } from 'react';
import {
  isActionSection,
  isCustomSection,
  isDynamicMediaSection,
  isPresetListSection,
  isMyWidgetsSection,
  isPresetSection,
  isRelatedAppsSection,
  isSiteMediaSection,
  isThemedSection,
  isWelcomeSection,
  shouldRenderSectionOnSearchQuery,
  isLogoSection,
} from '../../../utils/entities/sections';
import { ActionsDispatcherProvider } from '../../ActionsDispatcher';
import { useCurrentCategory } from '../../CurrentCategoryProvider';
import { LocaleProvider } from '../../LocaleProvider';
import { useRelatedApps } from '../../RelatedAppsProvider';
import { SkinProvider } from '../../Skin';
import { useUtils, UtilsProvider } from '../../UtilsProvider';
import { SectionViewParentType } from '../../UtilsProvider/UtilsProvider.types';
import { useProcessedSection } from './hooks/useProcessedSection';
import { SectionFooter } from './SectionFooter';
import { SectionHeader } from './SectionHeader';
import { ActionSection as ActionSectionComponent } from './sections/ActionSection';
import { CustomSection as CustomSectionComponent } from './sections/CustomSection';
import {
  PresetListSection as PresetListSectionComponent,
  PresetSection as PresetSectionComponent,
} from './sections/PresetSection';
import { MyWidgets as MyWidgetsSectionComponent } from './sections/MyWidgets';
import { RelatedApps } from './sections/RelatedApps/RelatedApps';
import { DynamicMediaSection } from './sections/DynamicMediaSection';
import { ThemedSection as ThemedSectionComponent } from './sections/ThemedSection';
import { WelcomeSection as WelcomeSectionComponent } from './sections/WelcomeSection';
import { LogoSection } from './sections/LogoSection';
import styles from './SectionView.scss';
import type { SectionViewInnerProps, SectionViewProps } from './SectionView.types';
import type { MyWidgetsSection } from '@wix/add-panel-common';

export const SectionViewInner: FC<SectionViewInnerProps> = ({
  section: _section,
  index: sectionIndex,
  className,
  style,
  headerStyle,
  addPanelSearchQuery,
  viewType,
  selectedItemSymbolName,
  selectedItemKey,
  shouldDisableTooltipOnHover = false,
  themedSectionParentType = SectionViewParentType.AddPanel,
  checkItemSelected,
  getExternalSectionComponent,
  notifyLayoutReady = () => undefined,
}) => {
  const { translate, hasSectionStyle } = useUtils();
  const category = useCurrentCategory();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const section = useProcessedSection(_section);

  const {
    systemId,
    type,
    sectionFooter,
    showSectionHeader,
    noSpaceBeforeSection,
    automationId,
    translations: { title, presetTitle = '', designPanelTitle },
    disableInfoIcon,
    props: { helpId },
  } = section;

  const { relatedApps } = useRelatedApps();

  useEffect(() => {
    if (!wrapperRef.current) {
      return;
    }

    notifyLayoutReady(systemId, wrapperRef.current);
  }, [notifyLayoutReady, systemId]);

  if (isThemedSection(section) && !hasSectionStyle(section)) {
    return null;
  }

  if (addPanelSearchQuery && !shouldRenderSectionOnSearchQuery(section)) {
    return null;
  }

  let headerText;
  if (viewType === 'design-panel') {
    headerText = designPanelTitle || title;
  } else {
    headerText = category?.hasSubcategory ? presetTitle : title;
  }

  const relatedAppsLoading = relatedApps === null && isRelatedAppsSection(section);
  const shouldShowSectionHeader = showSectionHeader && headerText && !relatedAppsLoading;

  const isFullWidthSection =
    isCustomSection(section) ||
    isActionSection(section) ||
    isPresetListSection(section) ||
    isThemedSection(section) ||
    isRelatedAppsSection(section);

  return (
    <div
      ref={wrapperRef}
      className={classnames(
        styles.sectionWrapper,
        {
          [styles.noSpaceBeforeSection]: noSpaceBeforeSection || !showSectionHeader,
          [styles.fullWidth]: isFullWidthSection,
        },
        className,
      )}
      data-section-title={translate(title)}
      data-tooltip-bounds="true"
      data-section-type={type}
      data-hook={`add-panel-section-view-wrapper-${automationId}`}
      style={style}
    >
      {shouldShowSectionHeader && (
        <SectionHeader
          className={classnames({
            [styles.noMarginBottom]:
              isCustomSection(section) || isActionSection(section) || isRelatedAppsSection(section),
          })}
          headerText={headerText}
          title={title}
          disableInfoIcon={disableInfoIcon}
          style={headerStyle}
          helpId={helpId}
          dataHook="add-panel-section-view-section-header"
        />
      )}

      {isActionSection(section) ? (
        <ActionSectionComponent section={section} />
      ) : isThemedSection(section) ? (
        <ThemedSectionComponent
          section={section}
          parentType={themedSectionParentType}
          selectedItemKey={selectedItemKey}
          checkItemSelected={checkItemSelected}
        />
      ) : isPresetSection(section) ? (
        <PresetSectionComponent
          section={section}
          index={sectionIndex}
          selectedItemKey={selectedItemKey}
          checkItemSelected={checkItemSelected}
          selectedItemSymbolName={selectedItemSymbolName}
          shouldDisableTooltipOnHover={shouldDisableTooltipOnHover}
        />
      ) : isPresetListSection(section) ? (
        <PresetListSectionComponent section={section} />
      ) : isMyWidgetsSection(section) ? (
        <MyWidgetsSectionComponent section={section as MyWidgetsSection} />
      ) : isCustomSection(section) ? (
        <CustomSectionComponent section={section} />
      ) : isWelcomeSection(section) ? (
        <WelcomeSectionComponent section={section} />
      ) : isLogoSection(section) ? (
        <LogoSection section={section} Component={getExternalSectionComponent?.(section) as FC} />
      ) : isSiteMediaSection(section) ? (
        <DynamicMediaSection section={section} Component={getExternalSectionComponent?.(section) as FC} />
      ) : isDynamicMediaSection(section) ? (
        <DynamicMediaSection
          section={section}
          key={addPanelSearchQuery}
          query={addPanelSearchQuery}
          Component={getExternalSectionComponent?.(section) as FC}
        />
      ) : isRelatedAppsSection(section) ? (
        relatedApps ? (
          <RelatedApps section={section} />
        ) : null
      ) : (
        <div data-hook="section-not-supported">Section is not supported yet</div>
      )}

      {sectionFooter && !sectionFooter.hide && (
        <SectionFooter footer={sectionFooter} section={section} className={styles.footer} />
      )}
    </div>
  );
};

export const SectionView: FC<SectionViewProps> = ({ utils, skin, onAction, locale, ...sectionViewProps }) => (
  <UtilsProvider utils={utils}>
    <ActionsDispatcherProvider onAction={onAction}>
      <LocaleProvider locale={locale}>
        <SkinProvider skin={skin}>
          <SectionViewInner {...sectionViewProps} />
        </SkinProvider>
      </LocaleProvider>
    </ActionsDispatcherProvider>
  </UtilsProvider>
);
